import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, ArrowUpDown } from "lucide-react";

const PERIODS = [
  { id: "all", label: "All Time" },
  { id: "30d", label: "30 Days" },
  { id: "60d", label: "60 Days" },
  { id: "90d", label: "90 Days" },
  { id: "6m", label: "6 Months" },
  { id: "12m", label: "12 Months" },
];

const TRENDS = {
  growing: { label: "Growing", cls: "text-status-available" },
  stable: { label: "Stable", cls: "text-charcoal" },
  declining: { label: "Declining", cls: "text-status-sold" },
  new: { label: "New", cls: "text-terracotta" },
  none: { label: "—", cls: "text-muted-foreground" },
  insufficient_data: { label: "Insufficient data", cls: "text-muted-foreground" },
};

const fmtSqft = (n) => (Number.isFinite(n) ? `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })} sq ft` : "—");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

function SortHeader({ label, sortKey, active, dir, onSort, testid }) {
  return (
    <th className="px-4 py-3 text-left">
      <button type="button" data-testid={testid} onClick={() => onSort(sortKey)} className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-warmgrey hover:text-charcoal">
        {label}
        <ArrowUpDown className={`h-3 w-3 ${active === sortKey ? "text-terracotta" : ""}`} />
        {active === sortKey && <span className="sr-only">{dir === "desc" ? "descending" : "ascending"}</span>}
      </button>
    </th>
  );
}

export default function AdminAnalytics() {
  const [period, setPeriod] = useState("all");
  const [dealerId, setDealerId] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [color, setColor] = useState("");
  const [format, setFormat] = useState("");
  const [overview, setOverview] = useState(null);
  const [dealers, setDealers] = useState(null);
  const [products, setProducts] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ dealers: [], states: [], colors: [], formats: [] });
  const [sortKey, setSortKey] = useState("net_revenue_usd");
  const [sortDir, setSortDir] = useState("desc");
  const [engagement, setEngagement] = useState(null);
  const [showAllProducts, setShowAllProducts] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/dealers"), api.get("/products")])
      .then(([dRes, pRes]) => {
        const dealerRows = dRes.data || [];
        const states = new Set();
        for (const d of dealerRows) {
          for (const l of d.locations || []) {
            const st = l.address?.state;
            if (st) states.add(st);
          }
        }
        const colors = new Set();
        const formats = new Set();
        for (const p of pRes.data || []) {
          for (const v of p.variants || []) {
            if (v.color_name) colors.add(v.color_name);
            if (v.format_in) formats.add(v.format_in);
          }
        }
        setFilterOptions({
          dealers: dealerRows,
          states: [...states].sort(),
          colors: [...colors].sort(),
          formats: [...formats].sort(),
        });
      })
      .catch(() => toast.error("Failed to load filter options"));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("period", period);
    if (dealerId) params.set("dealer_id", dealerId);
    if (stateFilter) params.set("state", stateFilter);
    if (color) params.set("color", color);
    if (format) params.set("format", format);
    const q = params.toString();
    setOverview(null);
    Promise.all([
      api.get(`/admin/analytics/overview?${q}`),
      api.get(`/admin/analytics/dealers?${q}`),
      api.get(`/admin/analytics/products?${q}`),
    ])
      .then(([o, d, p]) => {
        setOverview(o.data);
        setDealers(d.data);
        setProducts(p.data);
      })
      .catch(() => toast.error("Failed to load analytics"));
  }, [period, dealerId, stateFilter, color, format]);

  useEffect(() => {
    api.get(`/admin/analytics/engagement?period=${period}`)
      .then((r) => setEngagement(r.data))
      .catch(() => setEngagement(null));
  }, [period]);

  const onSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  // §6: collapse the zero-activity wall by default — products with sales, demand,
  // inventory, reservations or incoming stock stay visible; "Show all" reveals the rest.
  const visibleProducts = showAllProducts
    ? products || []
    : (products || []).filter((p) => p.net_revenue_usd > 0 || p.sqft_sold > 0 || p.order_count > 0 || p.florida_available_sqft > 0 || p.incoming_sqft > 0 || p.reservation_sqft > 0);

  const sortedDealers = useMemo(() => {
    if (!dealers) return [];
    const rows = [...dealers];
    rows.sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === "desc" ? -cmp : cmp;
    });
    return rows;
  }, [dealers, sortKey, sortDir]);

  const cards = overview
    ? [
        { testid: "card-net-revenue", label: "Net Revenue", value: formatMoney(overview.net_revenue_usd) },
        { testid: "card-orders", label: "Orders", value: overview.orders },
        { testid: "card-sqft", label: "Sq Ft Sold", value: fmtSqft(overview.sqft_sold) },
        { testid: "card-aov", label: "Avg Order Value", value: formatMoney(overview.avg_order_value_usd) },
        { testid: "card-active-dealers", label: "Active Dealers", value: overview.active_dealers },
        { testid: "card-active-locations", label: "Active Locations", value: overview.active_locations },
      ]
    : [];

  const selectCls = "h-10 bg-white";

  return (
    <div data-testid="admin-analytics-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-medium tracking-tight text-charcoal sm:text-5xl">Analytics</h1>
          <p className="mt-2 text-sm text-muted-foreground">Business performance — paid orders, fulfillment, samples, and product demand.</p>
        </div>
        <div className="flex flex-wrap gap-1 rounded-lg border border-border bg-white p-1" data-testid="period-selector">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`period-${p.id}`}
              onClick={() => setPeriod(p.id)}
              className={`rounded-md px-3 py-2 text-xs transition-colors duration-200 sm:px-4 sm:text-sm ${
                period === p.id ? "bg-terracotta font-medium text-white" : "text-warmgrey hover:text-charcoal"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3" data-testid="analytics-filters">
        <Select value={dealerId || "all"} onValueChange={(v) => setDealerId(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-dealer" className={`w-52 ${selectCls}`}><SelectValue placeholder="All dealers" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All dealers</SelectItem>
            {filterOptions.dealers.map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.dba || d.legal_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={stateFilter || "all"} onValueChange={(v) => setStateFilter(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-state" className={`w-36 ${selectCls}`}><SelectValue placeholder="All states" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All states</SelectItem>
            {filterOptions.states.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={color || "all"} onValueChange={(v) => setColor(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-color" className={`w-44 ${selectCls}`}><SelectValue placeholder="All colors" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All colors</SelectItem>
            {filterOptions.colors.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={format || "all"} onValueChange={(v) => setFormat(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-format" className={`w-36 ${selectCls}`}><SelectValue placeholder="All formats" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All formats</SelectItem>
            {filterOptions.formats.map((f) => (
              <SelectItem key={f} value={f}>{f}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!overview ? (
        <div className="flex min-h-[30vh] items-center justify-center" data-testid="analytics-loading">
          <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
        </div>
      ) : (
        <>
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6" data-testid="overview-cards">
            {cards.map((c) => (
              <Card key={c.testid} className="border-border bg-white" data-testid={c.testid}>
                <CardContent className="p-5">
                  <p className="num text-2xl font-medium text-charcoal">{c.value}</p>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-warmgrey">{c.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground" data-testid="kpi-definitions-note">"Active" dealers / locations = with paid orders in the selected period.</p>
          {overview.orders === 0 ? (
            <Card className="mt-6 border-border bg-white" data-testid="analytics-compact-empty">
              <CardContent className="p-8 text-center">
                <p className="text-sm font-medium text-charcoal">No activity in this period yet</p>
                <p className="mt-1 text-xs text-muted-foreground">Dealer performance, product performance and portal engagement will appear here as paid orders and portal activity begin.</p>
              </CardContent>
            </Card>
          ) : (
          <>
          <h2 className="mt-10 font-display text-2xl font-medium text-charcoal">Dealer Performance</h2>
          {sortedDealers.length === 0 ? (
            <Card className="mt-4 border-border bg-white"><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="dealer-table-empty">No dealer activity in this period.</CardContent></Card>
          ) : (
            <div className="mt-4 hidden overflow-x-auto rounded-xl border border-border bg-white md:block" data-testid="dealer-table">
              <table className="w-full min-w-[900px] text-sm">
                <thead className="border-b border-border bg-sand/40">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-warmgrey">Dealer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-warmgrey">Locations</th>
                    <SortHeader label="Revenue" sortKey="net_revenue_usd" active={sortKey} dir={sortDir} onSort={onSort} testid="sort-revenue" />
                    <SortHeader label="Orders" sortKey="orders" active={sortKey} dir={sortDir} onSort={onSort} testid="sort-orders" />
                    <SortHeader label="Sq Ft" sortKey="sqft_sold" active={sortKey} dir={sortDir} onSort={onSort} testid="sort-sqft" />
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-warmgrey">AOV</th>
                    <SortHeader label="Samples" sortKey="samples_shipped" active={sortKey} dir={sortDir} onSort={onSort} testid="sort-samples" />
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-warmgrey">Projects</th>
                    <SortHeader label="Pipeline" sortKey="pipeline_sqft" active={sortKey} dir={sortDir} onSort={onSort} testid="sort-pipeline" />
                    <SortHeader label="Last Business Activity" sortKey="last_activity" active={sortKey} dir={sortDir} onSort={onSort} testid="sort-last-activity" />
                  </tr>
                </thead>
                <tbody>
                  {sortedDealers.map((d) => (
                    <tr key={d.dealer_id} className="border-b border-border last:border-0 hover:bg-sand/30" data-testid={`dealer-row-${d.dealer_id}`}>
                      <td className="px-4 py-3">
                        <Link to={`/admin/dealers/${d.dealer_id}`} className="font-medium text-charcoal hover:text-terracotta" data-testid={`dealer-link-${d.dealer_id}`}>
                          {d.dealer_name}
                        </Link>
                        <p className="text-xs text-muted-foreground">{d.dealer_code}</p>
                      </td>
                      <td className="px-4 py-3">{d.locations}</td>
                      <td className="px-4 py-3 font-medium">{formatMoney(d.net_revenue_usd)}</td>
                      <td className="px-4 py-3">{d.orders}</td>
                      <td className="px-4 py-3">{fmtSqft(d.sqft_sold)}</td>
                      <td className="px-4 py-3">{formatMoney(d.avg_order_value_usd)}</td>
                      <td className="px-4 py-3">{d.samples_shipped}</td>
                      <td className="px-4 py-3">{d.active_projects}</td>
                      <td className="px-4 py-3">{fmtSqft(d.pipeline_sqft)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{fmtDate(d.last_activity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {sortedDealers.length > 0 && (
            <div className="mt-4 space-y-3 md:hidden" data-testid="dealer-cards">
              {sortedDealers.map((d) => (
                <Card key={d.dealer_id} className="border-border bg-white" data-testid={`dealer-card-${d.dealer_id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between gap-3">
                      <Link to={`/admin/dealers/${d.dealer_id}`} className="font-medium text-charcoal hover:text-terracotta" data-testid={`dealer-card-link-${d.dealer_id}`}>
                        {d.dealer_name}
                      </Link>
                      <p className="num text-sm font-medium text-charcoal">{formatMoney(d.net_revenue_usd)}</p>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">{d.dealer_code}</p>
                    <div className="num mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-charcoal">
                      <span>{d.orders} orders</span>
                      <span>{fmtSqft(d.sqft_sold)}</span>
                      <span>{d.samples_shipped} samples</span>
                      <span>{d.active_projects} projects</span>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">Last business activity: {fmtDate(d.last_activity)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-2xl font-medium text-charcoal">Product Performance</h2>
            {products && products.length > 0 && (
              <button type="button" data-testid="toggle-all-products" onClick={() => setShowAllProducts((v) => !v)} className="text-xs font-medium text-terracotta hover:underline">
                {showAllProducts ? "Show products with activity only" : `Show all ${products.length} products`}
              </button>
            )}
          </div>
          {!products || products.length === 0 ? (
            <Card className="mt-4 border-border bg-white"><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="product-table-empty">No product sales data yet.</CardContent></Card>
          ) : (
            <div className="mt-4 hidden overflow-x-auto rounded-xl border border-border bg-white md:block" data-testid="product-table">
              <table className="w-full min-w-[1100px] text-sm">
                <thead className="border-b border-border bg-sand/40">
                  <tr>
                    {["SKU", "Product", "Color", "Format", "Revenue", "Sq Ft", "Boxes", "Orders", "Samples", "Reserved", "FL Available", "Incoming", "30-Day Velocity", "Trend"].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-warmgrey">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleProducts.map((p) => {
                    const trend = TRENDS[p.trend] || TRENDS.none;
                    return (
                      <tr key={p.variant_id} className="border-b border-border last:border-0 hover:bg-sand/30" data-testid={`product-row-${p.sku}`}>
                        <td className="px-4 py-3 font-medium text-charcoal">{p.sku}</td>
                        <td className="px-4 py-3">{p.product_name || "—"}</td>
                        <td className="px-4 py-3">{p.color || "—"}</td>
                        <td className="px-4 py-3">{p.format || "—"}</td>
                        <td className="px-4 py-3 font-medium">{formatMoney(p.net_revenue_usd)}</td>
                        <td className="px-4 py-3">{fmtSqft(p.sqft_sold)}</td>
                        <td className="px-4 py-3">{p.boxes_sold}</td>
                        <td className="px-4 py-3">{p.order_count}</td>
                        <td className="px-4 py-3">{p.samples_shipped_units}</td>
                        <td className="px-4 py-3">{fmtSqft(p.reservation_sqft)}</td>
                        <td className="px-4 py-3">{fmtSqft(p.florida_available_sqft)}</td>
                        <td className="px-4 py-3">{fmtSqft(p.incoming_sqft)}</td>
                        <td className="px-4 py-3">{p.velocity_30d_sqft_per_day > 0 ? `${p.velocity_30d_sqft_per_day} sq ft/day` : "—"}</td>
                        <td className={`px-4 py-3 ${trend.cls}`} data-testid={`product-trend-${p.sku}`}>{trend.label}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {products && products.length > 0 && (
            <div className="mt-4 space-y-3 md:hidden" data-testid="product-cards">
              {visibleProducts.map((p) => {
                const trend = TRENDS[p.trend] || TRENDS.none;
                return (
                  <Card key={p.variant_id} className="border-border bg-white" data-testid={`product-card-${p.sku}`}>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between gap-3">
                        <p className="num text-sm font-semibold text-charcoal">{p.sku}</p>
                        <span className={`text-xs font-medium ${trend.cls}`} data-testid={`product-card-trend-${p.sku}`}>{trend.label}</span>
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">{[p.color, p.format].filter(Boolean).join(" · ") || "—"}</p>
                      <div className="num mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-charcoal">
                        <span>Revenue: {formatMoney(p.net_revenue_usd)}</span>
                        <span>Sq ft: {fmtSqft(p.sqft_sold)}</span>
                        <span>Orders: {p.order_count}</span>
                        <span>FL available: {fmtSqft(p.florida_available_sqft)}</span>
                        <span>Incoming: {fmtSqft(p.incoming_sqft)}</span>
                        <span>Reserved: {fmtSqft(p.reservation_sqft)}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          <h2 className="mt-10 font-display text-2xl font-medium text-charcoal">Portal Engagement</h2>
          <p className="mt-1 text-xs text-muted-foreground">Portal usage only — business activity is shown above. Test data excluded.</p>
          {!engagement ? null : engagement.invited_users === 0 && engagement.dealers.every((d) => !d.last_portal_activity) ? (
            <Card className="mt-4 border-border bg-white"><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="engagement-empty">No portal activity recorded yet. Engagement populates as dealers sign in and browse the catalog.</CardContent></Card>
          ) : (
            <>
              <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="engagement-cards">
                {[
                  { testid: "eng-activated", label: "Activated Users", value: `${engagement.activated_users} / ${engagement.invited_users}` },
                  { testid: "eng-active-users", label: "Active Users (period)", value: engagement.active_users_period },
                  { testid: "eng-engaged-dealers", label: "Dealers Engaged (period)", value: engagement.engaged_dealers_period },
                  { testid: "eng-paid-orders", label: "Paid Orders (period)", value: engagement.business.paid_orders },
                ].map((c) => (
                  <Card key={c.testid} className="border-border bg-white" data-testid={c.testid}>
                    <CardContent className="p-5">
                      <p className="num text-2xl font-medium text-charcoal">{c.value}</p>
                      <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-warmgrey">{c.label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <p className="num mt-3 text-xs text-muted-foreground" data-testid="engagement-funnel">
                In period: {engagement.business.projects_created} projects · {engagement.business.samples_requested} samples · {engagement.business.reservations_created} reservations · {engagement.business.orders_created} orders · {engagement.business.paid_orders} paid
              </p>
              <div className="mt-4 space-y-2" data-testid="engagement-dealers">
                {engagement.dealers.map((d) => (
                  <div key={d.dealer_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-white px-4 py-3 text-sm" data-testid={`engagement-row-${d.dealer_id}`}>
                    <Link to={`/admin/dealers/${d.dealer_id}`} className="font-medium text-charcoal hover:text-terracotta">{d.dealer_name}</Link>
                    <div className="num flex flex-wrap gap-x-4 text-xs text-muted-foreground">
                      <span>Last portal: {fmtDate(d.last_portal_activity)}</span>
                      <span>{d.active_days} active days</span>
                      <span>{d.logins} logins · {d.catalog_views} catalog · {d.product_views} products · {d.searches} searches</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          </>
          )}
        </>
      )}
    </div>
  );
}
